import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from '../utils/supabaseClient';

interface BillingContextType {
    stripe: Stripe | null;
    isLoading: boolean;
    redirectToCheckout: (docType: 'cpf' | 'cnpj', docNumber: string, phone: string) => Promise<void>;
    redirectToPortal: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initStripe = async () => {
            const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
            if (key) {
                const stripeInstance = await loadStripe(key);
                setStripe(stripeInstance);
            }
            setIsLoading(false);
        };
        initStripe();
    }, []);

    const redirectToCheckout = async (docType: 'cpf' | 'cnpj', docNumber: string, phone: string) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    doc_type: docType,
                    doc_number: docNumber,
                    phone_e164: phone,
                }),
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to create checkout session');

            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Checkout error:', error);
            throw error;
        }
    };

    const redirectToPortal = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to create portal session');

            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Portal error:', error);
            throw error;
        }
    };

    return (
        <BillingContext.Provider value={{ stripe, isLoading, redirectToCheckout, redirectToPortal }}>
            {children}
        </BillingContext.Provider>
    );
};

export const useBilling = () => {
    const context = useContext(BillingContext);
    if (context === undefined) {
        throw new Error('useBilling must be used within a BillingProvider');
    }
    return context;
};
